export interface GroupRestaurant {
  id: number;
  name: string;
  subdomainPrefix: string;
  address?: string;
  address2?: string;
  customStyle?: { bodyImage?: string; headerColor?: string } & Record<string, unknown>;
  // Deliberate escape hatch: tolerate unknown legacy API fields without breaking the type.
  [key: string]: unknown;
}

export interface RestaurantGroup {
  id: number;
  name: string;
  slug: string;
  logoImage?: string;
  groupLogoImage?: string;
  backgroundImage?: string;
  groupBackgroundImage?: string;
  groupBackgroundColor?: string | null;
  groupTextColor?: string | null;
  groupButtonsBackgroundColor?: string | null;
  groupButtonsTextColor?: string | null;
  groupGalleryBackgroundColor?: string | null;
  mainColor?: string | null;
  mainColorText?: string | null;
  googleTagManagerKey?: string;
  restaurants: GroupRestaurant[];
  // Deliberate escape hatch: tolerate unknown legacy API fields without breaking the type.
  [key: string]: unknown;
}

/**
 * Fetch one restaurant group by slug.
 * Endpoint mirrors the legacy Angular landing: GET /api/rest/v2/restaurant-groups/{slug}.
 *
 * Uses raw fetch (not the axios apiClient) because:
 *  - This is a public, unauthenticated endpoint — no auth headers needed.
 *  - The caller supplies the full apiBaseUrl; there is no tenant context to
 *    resolve yet (the group app loads before any restaurant subdomain is known).
 */
export async function fetchRestaurantGroup(
  slug: string,
  apiBaseUrl: string,
): Promise<RestaurantGroup> {
  const res = await fetch(`${apiBaseUrl}/api/rest/v2/restaurant-groups/${slug}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`restaurant-groups/${slug} failed: ${res.status}`);
  }
  const data = await res.json().catch(() => null);
  const group = data?.restaurantGroup;
  if (!group || !Array.isArray(group.restaurants)) {
    throw new Error(
      `restaurant-groups/${slug}: malformed response (missing restaurantGroup or restaurants[])`,
    );
  }
  return group as RestaurantGroup;
}
